import dayjs from 'dayjs';
import { IInput } from 'interfaces/IInput';
import type { IInputStep } from 'interfaces/IInputStep';
import {
  getDefaultInputTaskStatus,
  IInputTask,
  IInputTaskApplication,
  IInputTaskBase,
  IInputTaskCropEstablishment,
  IInputTaskGeneric,
  IInputTaskProduction,
  InputTaskCategory,
  InputTaskType,
} from 'interfaces/IInputTask';
import { PartialUserProfile } from 'interfaces/IUserProfile';
import { getUserDisplayName } from 'utils/UserUtils';
import { v4 as uuidv4 } from 'uuid';

export type TaskKind =
  | 'application'
  | 'production'
  | 'cropEstablishment'
  | 'otherCost';

export type ApplicationTaskFields = Omit<
  IInputTaskApplication,
  keyof IInputTaskBase | 'taskType'
>;
export type CropEstablishmentTaskFields = Omit<
  IInputTaskCropEstablishment,
  keyof IInputTaskBase | 'taskType'
>;

const assertNever = (value: never): never => {
  throw new Error(`Unsupported task kind: ${String(value)}`);
};

const buildBaseTask = <TTaskType extends InputTaskType>(
  input: IInput,
  taskType: TTaskType,
  category: InputTaskCategory,
  userProfile?: PartialUserProfile
): Omit<IInputTaskBase, 'taskType'> & { taskType: TTaskType } => {
  const now = new Date().toISOString();
  const userName = getUserDisplayName(
    userProfile?.firstName,
    userProfile?.lastName
  );

  return {
    taskId: uuidv4(),
    dateCreated: now,
    creatorId: userProfile?.id ?? null,
    snapCreator: userName || null,
    dateModified: now,
    modifierId: userProfile?.id ?? null,
    snapModifier: userName || null,
    startDate: null,
    endDate: null,
    area: input.zone?.zoneArea ?? null,
    areaUnit: input.zone?.areaUnit ?? null,
    timing: null,
    notes: null,
    category,
    status: getDefaultInputTaskStatus(category),
    startDateHasDay: false,
    endDateHasDay: false,
    chats: [],
    operator: null,
    taskCopyFrom: [],
    taskCopyTo: [],
    priority: 0,
    links: input.zone?.links ? [...input.zone.links] : [],
    steps: [],
    taskType,
  };
};

const buildSharedSprayTaskFields = () => ({
  waterRate: null,
  targetSpecies: [],
  plantCondition: null,
  applicationMethod: null,
  weather: null,
  weatherStats: [],
  soilMoisture: null,
  waterSource: null,
  sprayCapacity: null,
  sprayCapacityUnit: null,
  frost: null,
  frostUnit: null,
  isBuffZone: null,
  bandPercent: null,
  susceptibleCrops: [],
  plantBack: null,
  plantBackUnit: null,
  withholdPeriods: [],
  rainfast: null,
  rainfastUnit: null,
  coverPercent: null,
  drift: null,
  driftUnit: null,
  reentry: null,
  reentryUnit: null,
});

const buildApplicationTaskFields = (): ApplicationTaskFields => ({
  ...buildSharedSprayTaskFields(),
  nozzles: [],
});

const buildCropEstablishmentTaskFields = (): CropEstablishmentTaskFields => ({
  ...buildSharedSprayTaskFields(),
  nozzle: null,
});

const buildApplicationTask = (
  input: IInput,
  category: InputTaskCategory,
  userProfile?: PartialUserProfile
): IInputTaskApplication => {
  return {
    ...buildBaseTask(
      input,
      InputTaskType.Application,
      category,
      userProfile
    ),
    ...buildApplicationTaskFields(),
  };
};

const buildProductionTask = (
  input: IInput,
  category: InputTaskCategory,
  userProfile?: PartialUserProfile
): IInputTaskProduction => {
  return {
    ...buildBaseTask(input, InputTaskType.Production, category, userProfile),
  };
};

const buildCropEstablishmentTask = (
  input: IInput,
  category: InputTaskCategory,
  userProfile?: PartialUserProfile
): IInputTaskCropEstablishment => {
  return {
    ...buildBaseTask(
      input,
      InputTaskType.CropEstablishment,
      category,
      userProfile
    ),
    ...buildCropEstablishmentTaskFields(),
  };
};

// An other cost (OP-2399) is a generic task. Like other tasks it starts with
// the plan's area, which its $/ha costs are worked over and the user can
// change; it has no timing or application details.
const buildOtherCostTask = (
  input: IInput,
  category: InputTaskCategory,
  userProfile?: PartialUserProfile
): IInputTaskGeneric => {
  return {
    ...buildBaseTask(input, InputTaskType.Generic, category, userProfile),
  };
};

// `category` is the Plan/Actual view the task is added from, so a task added
// while viewing Actual stays in view (OP-2534).
export const buildTask = (
  input: IInput,
  kind: TaskKind,
  category: InputTaskCategory,
  userProfile?: PartialUserProfile
): IInputTask => {
  switch (kind) {
    case 'application':
      return buildApplicationTask(input, category, userProfile);
    case 'production':
      return buildProductionTask(input, category, userProfile);
    case 'cropEstablishment':
      return buildCropEstablishmentTask(input, category, userProfile);
    case 'otherCost':
      return buildOtherCostTask(input, category, userProfile);

    default:
      return assertNever(kind);
  }
};

/**
 * Copies a task as a new one (OP-2534): new task and step ids, fresh audit
 * fields, no chats or copy history. A Seed Treatment step linked to a Seed
 * step in the task is re-linked to that step's copy, so the pair stays
 * together.
 */
export const duplicateTask = (
  task: IInputTask,
  userProfile?: PartialUserProfile
): IInputTask => {
  const now = new Date().toISOString();
  const userName =
    getUserDisplayName(userProfile?.firstName, userProfile?.lastName) || null;
  const audit = {
    dateCreated: now,
    creatorId: userProfile?.id ?? null,
    snapCreator: userName,
    dateModified: now,
    modifierId: userProfile?.id ?? null,
    snapModifier: userName,
  };
  const sourceSteps = task.steps ?? [];
  const newStepIds = new Map(
    sourceSteps.map((step) => [step.stepId, uuidv4()])
  );
  const steps = sourceSteps.map((step): IInputStep => {
    const copy = {
      ...step,
      ...audit,
      stepId: newStepIds.get(step.stepId) ?? uuidv4(),
    };
    if ('linkedStepId' in copy && copy.linkedStepId) {
      return {
        ...copy,
        linkedStepId: newStepIds.get(copy.linkedStepId) ?? copy.linkedStepId,
      } as IInputStep;
    }
    return copy as IInputStep;
  });

  return {
    ...task,
    ...audit,
    taskId: uuidv4(),
    status: getDefaultInputTaskStatus(task.category),
    chats: [],
    taskCopyFrom: [],
    taskCopyTo: [],
    steps,
  };
};

export const formatDate = (
  dateValue: string | null,
  hasDay: boolean
): string => {
  if (!dateValue) return ' ';
  const date = dayjs(dateValue);
  if (!date.isValid()) return ' ';
  return hasDay ? date.format('MMMM DD, YYYY') : date.format('MMMM, YYYY');
};

export const compareTasksByEndDate = (a: IInputTask, b: IInputTask): number => {
  const aHasDate = a.endDate !== null && a.endDate !== undefined;
  const bHasDate = b.endDate !== null && b.endDate !== undefined;
  if (aHasDate !== bHasDate) return aHasDate ? -1 : 1;
  if (aHasDate && bHasDate) {
    const diff =
      new Date(a.endDate as string).getTime() -
      new Date(b.endDate as string).getTime();
    if (diff !== 0) return diff;
  }
  return a.category - b.category;
};
